import { api } from "@/lib/api";
import type {
  CreateFormationInput,
  CreateScheduleInput,
  CreateSessionInput,
  Formation,
  FormationFilters,
  FormationsPage,
  PublicCatalog,
  PublicFormation,
  Session,
  SessionFilters,
  SessionSchedule,
  UpdateFormationInput,
  UpdateScheduleInput,
  UpdateSessionInput,
} from "./types";

function qs(filters: Record<string, unknown>): string {
  const entries = Object.entries(filters).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (entries.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.set(k, String(v));
  return `?${sp.toString()}`;
}

// ─── Formations ─────────────────────────────────────────────────────────────

export const formationsApi = {
  list: (filters: FormationFilters = {}) =>
    api<FormationsPage>(`/api/v1/formations${qs(filters as Record<string, unknown>)}`),

  detail: (id: string) => api<Formation>(`/api/v1/formations/${id}`),

  create: (input: CreateFormationInput) =>
    api<Formation>("/api/v1/formations", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  update: (id: string, input: UpdateFormationInput) =>
    api<Formation>(`/api/v1/formations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  archive: (id: string) => api<Formation>(`/api/v1/formations/${id}`, { method: "DELETE" }),
};

// ─── Sessions ───────────────────────────────────────────────────────────────

export const sessionsApi = {
  list: (filters: SessionFilters = {}) =>
    api<Session[]>(`/api/v1/sessions${qs(filters as Record<string, unknown>)}`),

  detail: (id: string) => api<Session>(`/api/v1/sessions/${id}`),

  create: (input: CreateSessionInput) =>
    api<Session>("/api/v1/sessions", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  update: (id: string, input: UpdateSessionInput) =>
    api<Session>(`/api/v1/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  publish: (id: string) => api<Session>(`/api/v1/sessions/${id}/publish`, { method: "POST" }),

  cancel: (id: string, reason: string) =>
    api<Session>(`/api/v1/sessions/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  listSchedules: (id: string) => api<SessionSchedule[]>(`/api/v1/sessions/${id}/schedules`),

  addSchedule: (id: string, input: CreateScheduleInput) =>
    api<SessionSchedule>(`/api/v1/sessions/${id}/schedules`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateSchedule: (sessionId: string, scheduleId: string, input: UpdateScheduleInput) =>
    api<SessionSchedule>(`/api/v1/sessions/${sessionId}/schedules/${scheduleId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  deleteSchedule: (sessionId: string, scheduleId: string) =>
    api<void>(`/api/v1/sessions/${sessionId}/schedules/${scheduleId}`, {
      method: "DELETE",
    }),

  /** Lie une salle à la session et aux créneaux qui n'en ont pas encore. */
  async assignRoom(sessionId: string, roomId: string, roomName?: string): Promise<Session> {
    const updated = await sessionsApi.update(sessionId, {
      room_id: roomId,
      room: roomName,
    });
    const schedules = await sessionsApi.listSchedules(sessionId);
    await Promise.all(
      schedules
        .filter((slot) => !slot.room_id)
        .map((slot) => sessionsApi.updateSchedule(sessionId, slot.id, { room_id: roomId }))
    );
    return { ...updated, room_id: roomId, room: roomName ?? updated.room, room_name: roomName };
  },
};

// ─── Catalogue public (sans JWT) ────────────────────────────────────────────

export interface PublicStats {
  learners_trained_total: number;
  public_formations_count: number;
  upcoming_sessions_count: number;
  certified_partners_count: number;
}

export const publicCatalogApi = {
  catalog: (subdomain: string) =>
    api<PublicCatalog>(`/public/c/${encodeURIComponent(subdomain)}/catalog`),

  formation: (subdomain: string, formationId: string) =>
    api<PublicFormation>(`/public/c/${encodeURIComponent(subdomain)}/formations/${formationId}`),

  stats: (subdomain: string) =>
    api<PublicStats>(`/public/c/${encodeURIComponent(subdomain)}/stats`),
};
